<script setup lang="ts">
import ContactIcon from "@/Components/Icons/ContactIcon.vue";
import CostCodeIcon from "@/Components/Icons/CostCodeIcon.vue";
import CustomerBuilding from "@/Components/Icons/CustomerBuilding.vue";
import EquipmentIcon from "@/Components/Icons/EquipmentIcon.vue";
import GearSolid from "@/Components/Icons/GearSolid.vue";
import BillIcon from "@/Components/Icons/BillIcon.vue";
import MaterialIcon from "@/Components/Icons/MaterialIcon.vue";
import TimeCardIcon from "@/Components/Icons/TimeCardIcon.vue";
import UsersGearSolid from "@/Components/Icons/UsersGearSolid.vue";
import UserSolid from "@/Components/Icons/UserSolid.vue";
import VendorIcon from "@/Components/Icons/VendorIcon.vue";
import NavFlyoutButton from "@/Components/Nav/NavFlyoutButton.vue";
import NavLink from "@/Components/Nav/NavLink.vue";
import useGuard from "@/Composables/useGuard";
import type NavLinkType from "@/Types/NavLink";
import { computed } from "vue";

defineProps({
    expanded: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(["click-link"]);

const guard = useGuard();

const navLinks = computed((): NavLinkType[] => [
    {
        label: "Projects",
        icon: BillIcon,
        visible: true,
        active: route().current("projects.*"),
        href: route("projects.index"),
    },
    {
        label: "Customers",
        icon: CustomerBuilding,
        visible: true,
        active: route().current("customers.*"),
        href: route("customers.index"),
    },
    {
        label: "Vendors",
        icon: VendorIcon,
        visible: true,
        active: route().current("vendors.*"),
        href: route("vendors.index"),
    },
    {
        label: "PO",
        icon: BillIcon,
        visible: true,
        active: route().current("purchase-orders.*"),
        href: route("purchase-orders.index"),
    },
    {
        label: "Bills",
        icon: BillIcon,
        visible: true,
        active: route().current("bills.*"),
        href: route("bills.index"),
    },
    {
        label: "Invoices",
        icon: BillIcon,
        visible: true,
        active: route().current("invoices.*"),
        href: route("invoices.index"),
    },
    {
        label: "Contacts",
        icon: ContactIcon,
        visible: true,
        active: route().current("contacts.*"),
        href: route("contacts.index"),
    },
    {
        label: "Time Cards",
        icon: TimeCardIcon,
        visible: true,
        active: route().current("time-cards.*"),
        href: route("time-cards.index"),
    },

    {
        label: "Settings",
        icon: GearSolid,
        visible: guard.hasAny(
            "view cost codes",
            "view equipment",
            "view materials",
            "view roles",
            "view trades",
            "view users",
        ),
        active: route().current("settings.*"),
        children: [
            {
                label: "Users",
                icon: UserSolid,
                visible: guard.can("view users"),
                href: route("settings.users.index"),
            },
            {
                label: "Roles",
                icon: UsersGearSolid,
                visible: guard.can("view roles"),
                href: route("settings.roles.index"),
            },
            {
                label: "Trades",
                icon: UsersGearSolid,
                visible: guard.can("view trades"),
                href: route("settings.trades.index"),
            },
            {
                label: "Equipment",
                icon: EquipmentIcon,
                visible: guard.can("view equipment"),
                href: route("settings.equipment.index"),
            },
            {
                label: "Materials",
                icon: MaterialIcon,
                visible: guard.can("view materials"),
                href: route("settings.materials.index"),
            },
            {
                label: "Cost Codes",
                icon: CostCodeIcon,
                visible: guard.can("view cost codes"),
                href: route("settings.cost-codes.index"),
            },
        ],
    },
]);
</script>

<template>
    <div>
        <div
            v-for="link in navLinks"
            :key="link.label"
        >
            <NavLink
                v-if="'href' in link && link.visible"
                :href="link.href"
                :active="link.active ?? false"
                :label="link.label"
                :expanded="expanded"
                :icon="link.icon"
                @click="emit('click-link')"
            />

            <NavFlyoutButton
                v-if="'children' in link && link.visible"
                :label="link.label"
                :expanded="expanded"
                :icon="link.icon"
                :active="link.active"
                :children="link.children"
                @click-link="emit('click-link')"
            />
        </div>
    </div>
</template>
